import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  Calendar,
  Clock,
  Phone,
  MessageCircle,
  Mail,
  CheckCircle2,
  Plane,
} from "lucide-react";
import dayjs from "dayjs";

import { get, ref } from "firebase/database";

import { database } from "../firebase/firebase";
import LoadingScreen from "../utils/LoadingScreen";
import logo from "../assets/images/logo.png";
import BrazilianPlate from "../components/BrazilianPlate";
import { Button } from "../components/ui/button";
import { useCatalog } from "../contexts/CatalogContext";
import { CONTACT_EMAIL, WHATSAPP_NUMBER, whatsappUrl } from "../config/contact";

const contactLinks = [
  {
    icon: <MessageCircle className="h-4 w-4" />,
    href: whatsappUrl(),
    label: "WhatsApp",
  },

  {
    icon: <Mail className="h-6 w-6" />,
    href: `mailto:${CONTACT_EMAIL}`,
    label: "Email",
  },
  {
    icon: <Phone className="h-6 w-6" />,
    href: `tel://${WHATSAPP_NUMBER}`,
    label: "Telefone",
  },
];

export default function CheckinPage() {
  const [booking, setBooking] = useState<any>();
  const [driver, setDriver] = useState<any>();
  const [vehicle, setVehicle] = useState<any>();

  const { driversMap, vehiclesMap } = useCatalog();

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      navigate("/");
      return;
    }
    get(ref(database, `booking/${id}`)).then((snapshot) => {
      if (snapshot.exists()) {
        setBooking(snapshot.val());
      } else {
        navigate("/");
      }
    });
  }, [id, navigate]);

  useEffect(() => {
    if (!booking) return;
    if (booking.driver && driversMap[booking.driver]) {
      setDriver(driversMap[booking.driver]);
    }
    if (booking.vehicle && vehiclesMap[booking.vehicle]) {
      setVehicle(vehiclesMap[booking.vehicle]);
    }
  }, [booking, driversMap, vehiclesMap]);

  if (!booking || !driver || !vehicle) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 px-4 py-8 md:py-12">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <Link to="/" className="inline-block">
            <img
              className="mx-auto h-12 w-auto"
              src={logo}
              alt="MB Floripa Turismo"
            />
          </Link>
        </div>

        {/* Ticket Card */}
        <div className="relative overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-slate-900/5">
          {/* Confirmation Badge */}
          <div className="bg-gradient-to-r from-primary to-blue-600 px-6 py-4 text-center">
            <div className="flex items-center justify-center gap-2 text-white">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-bold uppercase tracking-widest">
                Instruções de Embarque
              </span>
            </div>
          </div>

          {/* Ticket Content */}
          <div className="p-6 md:p-8">
            {/* Welcome Message */}
            <div className="mb-8 border-b border-slate-100 pb-6 text-center">
              <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
                {booking.name}
              </h1>
              <p className="mt-2 text-sm text-slate-500 font-medium">
                Seu motorista está confirmado e pronto para te buscar!
              </p>
              <div className="mt-4 inline-flex items-center rounded-full bg-slate-100 px-4 py-1.5 text-sm font-medium text-slate-700">
                <Calendar className="mr-2 h-4 w-4 text-primary" />
                <span>{dayjs(booking.date).format("DD/MM/YYYY")}</span>
                <span className="mx-2 text-slate-300">|</span>
                <Clock className="mr-2 h-4 w-4 text-primary" />
                <span>{dayjs(booking.date).format("HH:mm")}</span>
              </div>
            </div>

            {/* Driver Section */}
            <div className="mb-6 rounded-2xl bg-slate-50 p-6 ring-1 ring-slate-900/5">
              <div className="mb-4 flex items-center gap-2">
                <div className="h-1 flex-1 bg-slate-200"></div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary">
                  Seu Motorista
                </span>
                <div className="h-1 flex-1 bg-slate-200"></div>
              </div>

              <div className="flex flex-col items-center gap-6 md:flex-row md:items-start">
                <div className="flex-shrink-0">
                  <img
                    src={driver.photo}
                    alt={driver.name}
                    className="h-24 w-24 rounded-full border-4 border-white object-cover shadow-md ring-1 ring-slate-200"
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-xl font-bold text-slate-900">
                    {driver.name}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Motorista profissional parceiro MB Floripa Turismo.
                  </p>
                </div>
              </div>
            </div>

            {/* Vehicle Section */}
            <div className="mb-8 rounded-2xl border border-slate-100 overflow-hidden bg-white shadow-sm">
              <div className="relative h-48 w-full md:h-56">
                <img
                  src={vehicle.photo}
                  alt={vehicle.name}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-bold drop-shadow-md">
                    {vehicle.name}
                  </h3>
                  <p className="text-sm font-medium opacity-90 drop-shadow-sm">
                    {vehicle.color}
                  </p>
                </div>
              </div>

              <div className="p-4 bg-slate-50 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="transform scale-90 md:scale-100 origin-left">
                  <BrazilianPlate plate={vehicle.plate} />
                </div>

                <div className="flex items-center gap-2 text-sm text-slate-600 font-medium">
                  <span className="truncate max-w-[100px]">
                    {booking.origin}
                  </span>
                  <span className="text-primary">→</span>
                  <span className="truncate max-w-[100px]">
                    {booking.destination}
                  </span>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mb-8 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-400 tracking-wide">
                      Seu Telefone
                    </p>
                    <p className="text-sm font-bold text-slate-900">
                      {booking.phone}
                    </p>
                  </div>
                </div>
              </div>

              {booking.flight && (
                <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Plane className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase text-slate-400 tracking-wide">
                        Voo
                      </p>
                      <p className="text-sm font-bold text-slate-900">
                        {booking.flight}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Separator */}
            <div className="my-8 relative">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true"
              >
                <div className="w-full border-t border-slate-200"></div>
              </div>
            </div>

            {/* Contact Section */}
            <div className="mb-6">
              <p className="mb-4 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                Precisa de Ajuda?
              </p>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {contactLinks.map((link) => (
                  <Button
                    key={link.label}
                    asChild
                    variant="outline"
                    className="h-20 flex-col gap-2 border-slate-200 bg-white hover:bg-slate-50 hover:border-primary/50 hover:text-primary transition-all shadow-sm rounded-xl"
                  >
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noreferrer"
                      className="flex flex-col items-center justify-center gap-2"
                    >
                      <div className="rounded-full bg-slate-100 p-2 text-slate-600 group-hover:bg-primary/10 group-hover:text-primary">
                        {link.icon}
                      </div>
                      <span className="text-xs font-bold">{link.label}</span>
                    </a>
                  </Button>
                ))}
              </div>
            </div>

            {/* Important Notice */}
            <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
              <p className="text-center text-xs font-medium text-blue-900 leading-relaxed">
                <strong className="block text-primary mb-1 text-sm">
                  Informação Importante
                </strong>
                Esteja no local de embarque com{" "}
                <strong>10 minutos de antecedência</strong>. Em caso de dúvidas,
                entre em contato pelo WhatsApp.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-slate-500">
          Desenvolvido por{" "}
          <a
            href="https://richardpompeo.com.br"
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-slate-700 hover:text-slate-900"
          >
            Richard Pompeo
          </a>
        </p>
      </div>
    </div>
  );
}
